import React from 'react';
import { GenerateOutlineFromTextModal } from './generate_outline_from_text_modal';
import { GenerateOutlineFromPhotoModal } from './generate_outline_from_photo_modal';
import { ReviseOutlineModal } from './revise_outline_modal';
import { saveTitle, watchCoverImage } from './save_title';

// @context TGW-2195 AIアシストとAI提案を同じ小さな印で識別できるようにする。
const SparkleIcon = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    style={{ display: 'block' }}
  >
    <path
      d="M12 2.75c.55 4.45 2.8 6.7 7.25 7.25-4.45.55-6.7 2.8-7.25 7.25-.55-4.45-2.8-6.7-7.25-7.25C9.2 9.45 11.45 7.2 12 2.75Z"
      fill="currentColor"
    />
    <path
      d="M19 15.5c.2 1.65 1.05 2.5 2.7 2.7-1.65.2-2.5 1.05-2.7 2.7-.2-1.65-1.05-2.5-2.7-2.7 1.65-.2 2.5-1.05 2.7-2.7Z"
      fill="currentColor"
    />
  </svg>
);

const SectionAiButtons = ({
  titleText,
  bodyText,
  setTitleText,
  setBodyText,
  setEvent,
  contents,
  setContents,
  updateTitleContent,
  updateTextContent,
  event,
  markAiAccepted,
}) => {
  // @context TGW-2195 AIの結果を反映した項目は「AIの提案」ピル・バナーの対象にする（PR #2536 Copilot 指摘）
  const setEventTitle = (newTitle) => {
    setEvent((prev) => ({ ...prev, title: newTitle }));
    setTitleText(newTitle);
    // @context TGW-2591 入口の「次へ」と同じ update_title でその場で保存する。
    //   紹介文は20秒ごとに自動保存されるのにタイトルには自動保存が無く、
    //   リロードするとタイトルだけ古い値に戻っていた。失敗しても画面の入力は消さない。
    //   カバー画像の作り直しは非同期なので、できあがったら差し替える（利用者はリロードしない）。
    //   まずタグ無しの画像が用意され、カテゴリの付け直しが追いつくとタグ付きに変わる。
    //   2回変わるので、そのつど差し替える。
    const previousCoverUrl = event.main_image_url_medium_strict;
    saveTitle(event.id, newTitle)
      .then(() =>
        watchCoverImage(event.id, newTitle, previousCoverUrl, (coverUrl) => {
          setEvent((prev) => ({ ...prev, main_image_url_medium_strict: coverUrl }));
        })
      )
      .catch(() => {});
    markAiAccepted?.('title');
  };
  const setEventBody = (newBody) => {
    setBodyText(newBody);
    updateTextContent?.(newBody);
    markAiAccepted?.('body');
  };
  const setEventDate = (newEventDate) => {
    setEvent((prev) => ({ ...prev, event_date: newEventDate }));
  };
  const setEventEndDatetime = (newEventEndDatetime) => {
    setEvent((prev) => ({ ...prev, event_end_datetime: newEventEndDatetime }));
  };
  const setPlace = (newPlace) => {
    setEvent((prev) => ({ ...prev, place: newPlace }));
  };
  const setMainImage = (newMainImageUrl) => {
    setEvent((prev) => ({
      ...prev,
      main_image_url_medium_strict: newMainImageUrl,
    }));
  };
  const setSubImage = (newSubImageContent) => {
    setContents((prev) => [...prev, newSubImageContent]);
  };

  const subImageCount = (contents || []).filter(
    (content) => content.content_type === 3 && content.photo_url_medium,
  ).length;

  return (
    <section id="ai_buttons">
      {/* @context TGW-2195 塗りの赤ボタン3つが「次へ」と同じ強さで必須入力より目立っていたため、
          紹介文カードの直下に置く小さな枠線ボタンへ格下げする。塗りの赤は「次へ」だけに使う。 */}
      <style>{`
        .tg-ai-actions { display: flex; flex-wrap: wrap; gap: 8px; }
        .tg-ai-actions .cursor-pointer > button.a-button {
          width: auto !important;
          min-height: 0 !important;
          margin: 0 !important;
          padding: 7px 14px !important;
          background: #fff !important;
          color: #FF6467 !important;
          border: 1px solid #F4C2C3 !important;
          border-radius: 999px !important;
          box-shadow: none !important;
          font-size: 12px !important;
          font-weight: 700 !important;
          line-height: 1.4 !important;
          white-space: nowrap;
        }
        .tg-ai-actions .cursor-pointer > button.a-button:focus:not(:focus-visible) { outline: none; }
        .tg-ai-actions .cursor-pointer > button.a-button:focus-visible { outline: 2px solid #FF6467; outline-offset: 2px; }
      `}</style>
      <div className="container col-md-8 col-lg-6" style={{ paddingTop: 2 }}>
        <div style={{ padding: '0 2px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              color: '#FF6467',
              margin: '0 0 8px',
            }}
          >
            <SparkleIcon size={13} />
            <span style={{ fontSize: 12, fontWeight: 700, color: '#666' }}>
              AIに手伝ってもらう
            </span>
          </div>
          <div className="tg-ai-actions">
            <GenerateOutlineFromTextModal
              onAcceptEventTitle={setEventTitle}
              onAcceptEventBody={setEventBody}
            />
            <GenerateOutlineFromPhotoModal
              eventId={event.id}
              mainImageUrl={event.main_image_url_medium_strict || ''}
              onAcceptEventTitle={setEventTitle}
              onAcceptEventBody={setEventBody}
              onAcceptEventDate={setEventDate}
              onAcceptEventEndDatetime={setEventEndDatetime}
              onAcceptPlace={setPlace}
              onMainImageConfirmed={setMainImage}
              onSubImageConfirmed={setSubImage}
              subImageCount={subImageCount}
            />
            <ReviseOutlineModal
              originalTitle={titleText}
              originalBody={bodyText}
              eventId={event.id}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default SectionAiButtons;
